import {
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Link,
  makeStyles,
  Typography,
} from "@material-ui/core";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import * as Sentry from "@sentry/browser";
import clsx from "clsx";
import React, { useCallback, useMemo } from "react";
import { useAlert } from "react-alert";
import { useDispatch, useSelector } from "react-redux";

import { useHistory } from "react-router";
import { fetchEventDefs } from "../actions/eventDefs";
import { HIPAAModeTooltip } from "../components/HIPAAModeTooltip";
import FreshpaintUser from "../components/users/FreshpaintUser";
import Unauthorized from "../components/users/Unauthorized";
import useCurrentEnvironment from "../hooks/use-environment";
import useAccountMetadata from "../hooks/useAccountMetadata";
import { useAllSaasDestinations } from "../hooks/useAllSaasDestinations";
import useUsers from "../hooks/useUsers";
import Navigation from "../Navigation";
import { PHIAllowListContainer } from "../PHIAllowListPage/PHIAllowListContainer";
import { AllowListType } from "../PHIAllowListPage/types";
import { PropertySensitivityLevels } from "../types/global";
import { isAccountInHIPAAModeV2, makeRequest } from "../util";
import { DestinationSlug } from "./types";
import { useArrayOfDestinationsBySlug } from "../hooks/useArrayOfDestinations";

const useStyles = makeStyles((theme) => ({
  navigation: {
    paddingTop: 12,
  },
  card: {
    width: "50%",
    minWidth: 800,
  },
  cardContent: {
    padding: 0,
    "&:last-child": {
      paddingBottom: 0,
    },
  },
  lightText: {
    color: "#546E7A",
  },
  darkText: {
    color: "#263238",
  },
  breadcrumb: {
    marginTop: 5,
    marginBottom: 10,
  },
  titleWrapper: {
    display: "flex",
    alignItems: "center",
  },
  headerIcon: {
    marginRight: theme.spacing(2),
  },
  headerAction: {
    marginTop: 0,
  },
  body: {
    margin: 24,
  },
}));

const DestinationPHIAllowListPage: React.FC<{
  destinationSlug: DestinationSlug;
}> = ({ destinationSlug }) => {
  const alert = useAlert();
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const accountMetadata = useAccountMetadata();
  const currentEnvironment = useCurrentEnvironment();
  const allSaasDestinations = useAllSaasDestinations();

  const configuredDests = useArrayOfDestinationsBySlug(destinationSlug);
  const configuredDest = configuredDests[0];

  const usersState = useUsers();
  const destination = useMemo(() => allSaasDestinations[destinationSlug], [
    allSaasDestinations,
    destinationSlug,
  ]);

  const handleSave = async (
    allowListType: AllowListType,
    newSensitivityLevels: PropertySensitivityLevels
  ): Promise<void> => {
    if (!destination) {
      alert.error("Destination not found.");
      return;
    }

    return makeRequest("update_destination_phi_allowlist", {
      destination_type: destination.key,
      sensitivity_type: allowListType,
      sensitivity_levels: newSensitivityLevels,
    })
      .then(() => {
        dispatch(fetchEventDefs());
      })
      .catch((error: any) => {
        alert.error("Something went wrong. Unable to save allowlist.");
        console.log(error);
        Sentry.captureException(error);
      });
  };

  const category = useMemo(() => destination?.categoryDisplay(), [destination]);
  // TODO CJ This doesn't really work. Should maybe link back to the destination
  // list, perhaps filtered by "Mixpanel". Or maybe just remove it.
  const goToCategory = useCallback(
    () => history.push(`/destinations/${category}`),
    [category, history]
  );

  const goToDestination = useCallback(
    () => history.push(`/destinations/${category}/${destinationSlug}`),
    [category, history, destinationSlug]
  );

  if (!accountMetadata.loaded || !isAccountInHIPAAModeV2(accountMetadata)) {
    return null;
  }

  if (!configuredDest) {
    return null;
  }

  if (!destination || !destination.supportsServerSide) {
    return null;
  }

  const destinationName = destination?.name || "your destination";
  const title = `${destinationName}: PHI Allowlist`;

  const canAccess =
    !accountMetadata.config.rbac ||
    new FreshpaintUser(usersState.currentUser).hasAdminAccess();

  return (
    <Navigation className={clsx(classes.navigation)}>
      {usersState.loading ? (
        "Loading"
      ) : canAccess ? (
        <>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            className={clsx(classes.breadcrumb)}
          >
            <Link color="inherit" onClick={() => history.push(`/destinations`)}>
              <Typography
                className={clsx(classes.lightText)}
                variant="overline"
              >
                Destinations
              </Typography>
            </Link>
            <Link color="inherit" onClick={goToCategory}>
              <Typography
                className={clsx(classes.lightText)}
                variant="overline"
              >
                {destination?.categoryDisplay().toUpperCase()}
              </Typography>
            </Link>
            <Link color="inherit" onClick={goToDestination}>
              <Typography
                className={clsx(classes.lightText)}
                variant="overline"
              >
                {destination?.name.toUpperCase()}
              </Typography>
            </Link>
            <Typography className={clsx(classes.darkText)} variant="overline">
              PHI Allow List
            </Typography>
          </Breadcrumbs>
          <Card className={classes.card}>
            <CardHeader
              title={
                <div className={classes.titleWrapper}>
                  <destination.logoSquare
                    className={clsx(classes.headerIcon)}
                    height={24}
                    width={24}
                  />
                  {title}
                  <HIPAAModeTooltip dest={configuredDest} />
                </div>
              }
              action={
                <Button variant="outlined" onClick={() => history.goBack()}>
                  Back
                </Button>
              }
              classes={{ action: clsx(classes.headerAction) }}
            />
            <Divider />
            <CardContent className={clsx(classes.cardContent)}>
              <div className={clsx(classes.body)}>
                <Typography variant="body1">
                  Classify properties that are known to be safe to send to&nbsp;
                  {destinationName}.
                  <br />
                  <br />
                  Properties from the&nbsp;
                  <Link
                    color="initial"
                    className="text-brand-red"
                    onClick={() => history.push("/hipaa/allowlist")}
                  >
                    {currentEnvironment.project.name} project&apos;s allowlist
                  </Link>
                  &nbsp;will also be allowed.
                  <br />
                  <br />
                  Refer to the&nbsp;
                  <Link
                    color="initial"
                    className="text-brand-red"
                    href="https://documentation.freshpaint.io/hipaa-mode#enforced-allowlists"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    HIPAA Mode enforced allowlists docs
                  </Link>
                  &nbsp;for more details.
                  {configuredDests.length > 1 && (
                    <>
                      <br />
                      <br />
                      <strong>
                        There are {configuredDests.length} {destinationName}{" "}
                        destinations configured. These settings will apply to
                        all of them.
                      </strong>
                    </>
                  )}
                </Typography>
              </div>
              <Divider />
              <PHIAllowListContainer
                sensitivityLevels={{
                  event: configuredDest.event_property_sensitivity_levels ?? {},
                  user: configuredDest.user_property_sensitivity_levels ?? {},
                  group: configuredDest.group_property_sensitivity_levels ?? {},
                }}
                onSave={handleSave}
              />
            </CardContent>
          </Card>
        </>
      ) : (
        <Unauthorized message="Only Admin can view / update HIPAA Allowlist" />
      )}
    </Navigation>
  );
};

export default DestinationPHIAllowListPage;
